import React, { useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useAtomValue, useSetAtom } from 'jotai'

import * as lib from '../../lib'
import * as state from '../../states'
import { IFetchItemsSetup, IAccount } from '../../types'
import { AccountDeletedListTable } from './AccountDeletedListTable'
import { useAccountDeletedList, useFiltersAccountDeletedListModal, useSortAccountDeletedListModal } from '../../hooks'
import { ToolPanel, UiPagination, ESortOrder } from '../ui'
import { Filters, FiltersButtonIcon } from '../filters'
import { Access } from '../../lib'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'
import { toast } from 'react-toastify'


type IAccountDeletedListModalProps = {
	show: boolean;
	take: number | 'all';
	onClose: () => void;
}

export const AccountDeletedListModal = (
	props: IAccountDeletedListModalProps
) => {
	const { t } = useTranslation()
	const { show, take = 10, onClose } = props

	const profile = useAtomValue(state.profileAtom)
	const $access = profile ? new Access(profile) : null

	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const [page, setPage] = useState<number>(0)
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		accountDeletedFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useFiltersAccountDeletedListModal(setPage)

	const {
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useSortAccountDeletedListModal()

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useAccountDeletedList(setup, send)

	const onResurrect = async (item: IAccount, e: React.MouseEvent) => {
		//console.log('onResurrect!!!', { item })
		const accountId: number = item?.id ?? 0
		const login = item?.auth?.login ?? null
		const accountLabel = `'[id: ${accountId}] ${login ?? '?'}'`

		const uri: string = `account/${accountId}/resurrect`
		const method: string = 'PATCH'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)
		const body: string = JSON.stringify({})
		const options: lib.FetchOptions = { body, method, params, headers }

		try {
			setFetchIsLoading(state.turnOnLoader)
			const updated = await lib.fetching<{ result: boolean }>(uri, options)
			updated && toast.success(
				`${t(ALL_LANG_KEY.account_crud_modal.msg_resurrect_success, { account: accountLabel })}`,
				{
					autoClose: 5000,
					hideProgressBar: false
				}
			)
			warmUp()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
			toast.error(
				`${t(ALL_LANG_KEY.account_crud_modal.msg_resurrect_error, { account: accountLabel })}`,
				{
					autoClose: 5000,
					hideProgressBar: false
				}
			)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const modalTitle = t(ALL_LANG_KEY.account_crud_modal.title_deleted)

	return <>
		<Modal
			show={show}
			onHide={onClose}
			size="xl"
			scrollable={true}
			fullscreen={true}
		>
			<Modal.Header closeButton>
				<FiltersButtonIcon
					activeFiltersCount={activeFiltersCount}
					filterFillFactor={filterFillFactor}
					onClick={() => setIsFiltersPanelOpen((val) => !val)}
					styles={{ marginRight: 'auto' }}
				/>

				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body className={'scroll'}>
				<Filters
					classesToAdd={{ filters: 'filters-account-deleted-modal' }}
					config={accountDeletedFiltersConfig}
					initialFiltersValue={defaultFiltersValue}
					resetFiltersValue={initialFiltersValue}
					isAutoModeWithDebounce={false}
					isFiltersPanelOpen={isFiltersPanelOpen}
					isFiltersToClear={clearChildFilters}
					setFiltersCB={(value) => {
						setUserFilters(value)
					}}
				/>

				<AccountDeletedListTable
					items={items}
					skip={skip}
					sortBy={sortBy}
					setSortBy={setSortBy}
					order={order}
					setOrder={setOrder}
					onResurrect={onResurrect}
				/>
			</Modal.Body>

			<Modal.Footer>
				<ToolPanel flow=">-|-<" width={'100%'}>
					<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.account_crud_modal.btn_close)}`}</Button>

					<UiPagination
						page={page}
						take={take}
						total={total}
						click={(page: number, e: React.MouseEvent) => {
							e.preventDefault()
							setPage(page)
						}}
					/>
				</ToolPanel>

			</Modal.Footer>
		</Modal>
	</>
}
